import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo, useState } from 'react';
// routes
import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hook';
// api
import { usersGetProfile } from 'src/api/users';
// utils
import { RESPONSE_STATUS_SUCCESS, PAYMENT_STRIPE_STATUS } from 'src/constant';
// context
import { AuthContext } from './auth-context';
import { isValidToken, setSession, STORAGE_KEY } from './utils';
// socket
import { socketInstance } from 'src/sockets';
// stores
import { useCRMRecordStore } from 'src/stores/useCRMRecordStore';

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const REDUCER_ACTIONS = {
  INITIAL: 'INITIAL',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  UPDATE: 'UPDATE',
};

const authReducer = (state, action) => {
  if (action.type === REDUCER_ACTIONS.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === REDUCER_ACTIONS.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return {
    loading: false,
    user: action.payload.user,
  };
};

export function AuthProvider({ children }) {
  const router = useRouter();
  const pathname = usePathname();
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [socketConnect, setSocketConnect] = useState(false);
  // Store
  const { setCRMReset } = useCRMRecordStore();

  const initialize = useCallback(async () => {
    const accessToken = localStorage.getItem(STORAGE_KEY);
    if (accessToken && isValidToken(accessToken)) {
      if(!pathname.includes('crm') || !pathname.includes('account')) {
        setCRMReset();
      }
      setSession(accessToken, STORAGE_KEY);
      const response = await usersGetProfile();
      dispatch({
        type: REDUCER_ACTIONS.INITIAL,
        payload: {
          user: response.status === RESPONSE_STATUS_SUCCESS ? response.data : null,
        },
      });
      if(response.status === RESPONSE_STATUS_SUCCESS) {
        handleRedirectPayment(response.data.stripe_billing_status);
      }
    } else {
      dispatch({
        type: REDUCER_ACTIONS.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // SOCKETS
  useEffect(() => {
    if(state.user && !socketConnect) {
      setSocketConnect(true);
      socketInstance.io.opts.query = { userID: state.user.id };
      socketInstance.connect();
      socketInstance.on('changeStripeBillingStatus', ({ newStatus }) => {
        handleRedirectPayment(newStatus);
      });
      return () => {
        socketInstance.off('changeStripeBillingStatus');
        socketInstance.disconnect();
      };
    }
  }, [state]);

  // PAYMENT
  const handleRedirectPayment = useCallback((stripe_billing_status) => {
    if(PAYMENT_STRIPE_STATUS.includes(stripe_billing_status)) {
      router.replace(paths.dashboard.reactivate);
    }
  }, []);

  // LOGIN
  const contextLogin = useCallback((accessToken, user) => {
    setSession(accessToken, STORAGE_KEY);
    dispatch({
      type: REDUCER_ACTIONS.LOGIN,
      payload: {
        user,
      },
    });
    setCRMReset();
  }, []);

  // LOGOUT
  const contextLogout = useCallback(() => {
    setSession(null, STORAGE_KEY);
    dispatch({
      type: REDUCER_ACTIONS.LOGOUT,
    });
  }, []);

  // UPDATE
  const contextUpdate = useCallback((user) => {
    dispatch({
      type: REDUCER_ACTIONS.UPDATE,
      payload: {
        user,
      },
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      userSession: state.user,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      contextLogin,
      contextLogout,
      contextUpdate,
    }),
    [contextLogin, contextLogout, contextUpdate, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
